import {Alert} from 'src/design/atoms/Alert';
import {Center} from 'src/design/atoms/Center';
import {Loader} from 'src/design/atoms/Loader';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconAlertCircle} from '@tabler/icons-react';
import React, {useMemo, useState} from 'react';
import {InspectionStateEnum} from 'src/api/ProactApiClient';
import {SegmentedControl} from 'src/design/atoms/SegmentedControl';
import {Switch} from 'src/design/atoms/Switch';
import {NoItemsPanel} from 'src/design/molecules/NoItemsPanel';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {GroupedList} from 'src/design/molecules/GroupedList';
import {OfflinePlaceholder} from 'src/app/offline/OfflinePlaceholder';
import {useDownloadedInspections} from 'src/app/inspections/offline/useInspectionOfflineSession';
import {useConnectivity} from 'src/offline/useConnectivity';
import {useGetInspectionListV2Query} from 'src/redux/queries/inspection';
import {useAppSelector} from 'src/redux/hooks';
import {useInspectionPermissions} from 'src/shared/auth/permissions/useInspectionPermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {TimelineMode} from '../inspectionTimelineGrouping';
import {
  TIMELINE_MODE_OPTIONS,
  groupByTimeline,
} from '../inspectionTimelineGrouping';
import {InspectionCard} from './InspectionCard';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const LARGE_PAGE_LENGTH = 500;

type MyViewMode = 'list' | TimelineMode;

const VIEW_MODE_OPTIONS = [
  {label: 'List', value: 'list'},
  ...TIMELINE_MODE_OPTIONS,
];

// Base view scope is tenant-wide; "anyone" still narrows from it.
export const InspectionMyTab: React.FC = () => {
  const navigate = useAppNavigate();
  const perms = useInspectionPermissions();
  const currentUser = useAppSelector(state => state.session.user);
  const userId = currentUser?.id;

  const [assignedToAnyone, setAssignedToAnyone] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const [viewMode, setViewMode] = useState<MyViewMode>('list');

  const {isOffline} = useConnectivity();
  const downloaded = useDownloadedInspections();

  const {data, isLoading, isError, error} = useGetInspectionListV2Query(
    {
      start: 0,
      length: LARGE_PAGE_LENGTH,
      filters: [],
      filterFns: [],
      sorting: [{id: 'dueAt', desc: false}],
      manageView: false,
      assignedToAnyone,
      assigneeId: assignedToAnyone ? undefined : userId,
    },
    {skip: isOffline}
  );

  const items = useMemo(() => data?.data ?? [], [data]);

  const activeCount = useMemo(
    () =>
      items.filter(item => item.state !== InspectionStateEnum.Completed).length,
    [items]
  );

  const visibleItems = useMemo(
    () =>
      showCompleted
        ? items
        : items.filter(item => item.state !== InspectionStateEnum.Completed),
    [items, showCompleted]
  );

  const timelineGroups = useMemo(
    () =>
      viewMode === 'list'
        ? []
        : groupByTimeline(
            visibleItems,
            viewMode,
            item => item.scheduledStartAt
          ),
    [visibleItems, viewMode]
  );

  if (isOffline) {
    return (
      <Stack>
        <Text size="sm" c="dimmed">
          Showing inspections downloaded to this device.
        </Text>
        {downloaded === undefined ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : downloaded.length === 0 ? (
          <OfflinePlaceholder
            heading="No inspections downloaded"
            text="Download an inspection while you are connected to work on it offline."
          />
        ) : (
          <Stack gap="sm">
            {downloaded.map(item => (
              <InspectionCard
                key={item.id}
                inspection={item}
                isAssignedToMe={item.assigneeId === userId}
              />
            ))}
          </Stack>
        )}
      </Stack>
    );
  }

  return (
    <Stack>
      {currentUser && (
        <Text size="sm" c="dimmed">
          {currentUser.name}
          {currentUser.jobTitle ? ` (${currentUser.jobTitle})` : ''} ·{' '}
          {activeCount} active
        </Text>
      )}

      <TableToolbar>
        <TableToolbar.Filters>
          <SegmentedControl
            value={assignedToAnyone ? 'anyone' : 'me'}
            onChange={value => setAssignedToAnyone(value === 'anyone')}
            data={[
              {label: 'Assigned to me', value: 'me'},
              {label: 'Assigned to anyone', value: 'anyone'},
            ]}
          />
          <SegmentedControl
            value={viewMode}
            onChange={value => setViewMode(value as MyViewMode)}
            data={VIEW_MODE_OPTIONS}
          />
          <Switch
            label="Show completed"
            checked={showCompleted}
            onChange={event => setShowCompleted(event.currentTarget.checked)}
          />
        </TableToolbar.Filters>
        <TableToolbar.Actions>
          {perms.create && (
            <TableToolbar.Action
              variant="create"
              onClick={() => navigate(PathUtility.InspectionsCreate())}>
              New Inspection
            </TableToolbar.Action>
          )}
        </TableToolbar.Actions>
      </TableToolbar>

      {isError ? (
        <Alert
          color="red"
          variant="light"
          icon={<IconAlertCircle size={18} />}
          title="Could not load inspections">
          {parseErrorMessage(error)}
        </Alert>
      ) : isLoading ? (
        <Center py="xl">
          <Loader size="lg" />
        </Center>
      ) : visibleItems.length === 0 ? (
        <NoItemsPanel noItemsText="No inspections assigned." />
      ) : viewMode === 'list' ? (
        <Stack gap="sm">
          {visibleItems.map(item => (
            <InspectionCard
              key={item.id}
              inspection={item}
              isAssignedToMe={item.assigneeId === userId}
            />
          ))}
        </Stack>
      ) : (
        <GroupedList
          groups={timelineGroups}
          getItemKey={item => item.id}
          renderItem={item => (
            <InspectionCard
              inspection={item}
              isAssignedToMe={item.assigneeId === userId}
            />
          )}
        />
      )}
    </Stack>
  );
};
